import { useState } from 'react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Phone, PhoneOutgoing, Bot } from 'lucide-react';
import { useVapiConfig, useUpdateVapiConfig, useTriggerTestCall, useVapiCalls } from '@/hooks/useAdmin';
import { toast } from 'sonner';

export default function VapiConfigPage() {
  const { data: config } = useVapiConfig();
  const { data: calls = [] } = useVapiCalls();
  const updateConfig = useUpdateVapiConfig();
  const testCall = useTriggerTestCall();

  const [greeting, setGreeting] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [maxDuration, setMaxDuration] = useState(300);
  const [apiKey, setApiKey] = useState('');

  const initialized = config && !greeting && !voiceId;
  if (initialized) {
    setGreeting(config.default_greeting);
    setVoiceId(config.voice_id);
    setMaxDuration(config.max_call_duration);
  }

  const handleSave = () => {
    const data: any = { default_greeting: greeting, voice_id: voiceId, max_call_duration: maxDuration };
    if (apiKey) data.api_key = apiKey;
    updateConfig.mutate(data, { onSuccess: () => toast.success('VAPI settings saved') });
  };

  return (
    <>
      <PageHeader title="VAPI AI Agent" description="Configure the AI voice agent for outbound calls" />

      <div className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" /> Agent Configuration
              {config && (
                <Switch checked={config.is_active} onCheckedChange={(checked) => updateConfig.mutate({ is_active: checked })} className="ml-auto" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={config?.api_key_masked ?? '••••••••'} type="password" />
              <p className="text-xs text-muted-foreground">Leave blank to keep current key. Current: {config?.api_key_masked}</p>
            </div>
            <div className="space-y-2">
              <Label>Default Greeting</Label>
              <Textarea value={greeting} onChange={(e) => setGreeting(e.target.value)} rows={3} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Voice ID</Label>
                <Input value={voiceId} onChange={(e) => setVoiceId(e.target.value)} placeholder="e.g. rachel" />
              </div>
              <div className="space-y-2">
                <Label>Max Call Duration (seconds)</Label>
                <Input type="number" value={maxDuration} onChange={(e) => setMaxDuration(Number(e.target.value))} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={updateConfig.isPending}>Save Settings</Button>
              <Button variant="outline" onClick={() => testCall.mutate(undefined, { onSuccess: () => toast.success('Test call triggered!') })} disabled={testCall.isPending}>
                <PhoneOutgoing className="mr-2 h-4 w-4" /> Test Call
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Phone className="h-5 w-5" /> Call Log</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Transcript</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((call) => (
                  <TableRow key={call.id}>
                    <TableCell className="text-sm font-medium">{call.contact_name ?? call.contact_id}</TableCell>
                    <TableCell>
                      <Badge variant={call.direction === 'outbound' ? 'default' : 'secondary'} className="text-xs">{call.direction}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{Math.floor(call.duration_seconds / 60)}:{(call.duration_seconds % 60).toString().padStart(2, '0')}</TableCell>
                    <TableCell className="text-sm">{call.outcome}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{call.transcript_preview}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(call.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
